import { ArrowRight, Info } from "lucide-react";
import { useContext, useState } from "react";

import {
  captureProductEvent,
  productAnalyticsEvents,
} from "../analytics/events";
import ClusterStatus from "./ClusterStatus";
import CodeSnippet from "./CodeSnippet";
import IdentityProviderSettings from "./IdentityProviderSettings";
import { RadioCard } from "./RadioCard";
import WizardStep from "./WizardStep";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserContext } from "@/contexts/UserContext";
import sendStandardEvent from "@/hooks/use-firebase-event";
import { useSettings } from "@/hooks/use-settings";
import { useGetSettingsQuery } from "@/store/enhancedApi";
import {
  Cluster,
  Settings,
  useUpdateClusterMutation,
} from "@/store/pomeriumApi";

export type SystemType = "docker" | "linux" | "kubernetes" | "manual";

type ClusterOnboardingWizardProps = {
  cluster: Cluster | undefined;
  clusterId: string | undefined;
  clusterToken: string | undefined;
  organizationId: string | undefined;
  className?: string;

  onFinishStep: () => void;
  handlePrevWizard?: () => void; // an extra prev button to be added to the first step
  handleChangeSystemType?: (system: SystemType) => void;
  handleChangeAddressAndPort?: (
    address: string | undefined,
    port: string | undefined,
    system: SystemType | undefined,
  ) => void;
};

export default function ClusterOnboardingWizard({
  cluster,
  clusterId,
  clusterToken,
  organizationId,
  className,
  onFinishStep,
  handlePrevWizard,
  handleChangeSystemType,
  handleChangeAddressAndPort,
}: ClusterOnboardingWizardProps) {
  const { user } = useContext(UserContext);

  const [wizardStep, setWizardStep] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [systemType, setSystemType] = useState<SystemType>("docker");
  const [kubernetesLoadBalancerIp, setKubernetesLoadBalancerIp] = useState("");
  const [enableFinishStep, setEnableFinishStep] = useState(true);

  const { data: settings } = useGetSettingsQuery(
    {
      organizationId: organizationId || "",
      clusterId: clusterId || "",
    },
    {
      skip: !organizationId || !clusterId,
    },
  );

  const [updateCluster] = useUpdateClusterMutation();

  return (
    <>
      {wizardStep === 0 && (
        <WizardStep
          title="Let's Connect a Deployment"
          subtitle="Welcome to Pomerium Zero!"
          description="How do you want to host Pomerium?"
          onPrevStep={handlePrevWizard || undefined}
          onNextStep={() => setWizardStep(1)}
          className={className}
        >
          <SystemTypeCardOptions
            systemType={systemType}
            setSystemType={(system: SystemType) => {
              captureProductEvent(
                productAnalyticsEvents.activationInstallPathSelected,
                {
                  system_type: system,
                },
              );
              handleChangeSystemType?.(system);
              setSystemType(system);
            }}
          />
          <Card className="flex gap-2 items-center justify-between">
            <p>Have an existing Pomerium configuration?</p>
            <Button
              variant="default"
              className="flex gap-2 items-center"
              onClick={(event) => {
                event.preventDefault();
                setIsImporting(true);
                setWizardStep(1);
                sendStandardEvent(
                  "Import Existing Configuration",
                  "onboarding",
                  user,
                );
              }}
            >
              Import existing configuration
              <ArrowRight />
            </Button>
          </Card>
        </WizardStep>
      )}

      {wizardStep === 1 && (
        <WizardStep
          title="Save Your Cluster Token"
          subtitle="Welcome to Pomerium Zero!"
          description="Copy your token and save it somewhere safe."
          onPrevStep={() => {
            setIsImporting(false);
            setWizardStep(0);
          }}
          onNextStep={() => setWizardStep(2)}
          className={className}
        >
          <CodeSnippet
            code={clusterToken || ""}
            copyInfo="clusterToken"
            showViewToggle
          />
        </WizardStep>
      )}

      {wizardStep === 2 && (
        <WizardStep
          title="Configure Identity Provider"
          subtitle="Welcome to Pomerium Zero!"
          description="Authenticate with our hosted service or roll your own"
          onPrevStep={() => setWizardStep(1)}
          onNextStep={() => setWizardStep(3)}
          enableNextStep={enableFinishStep}
          className={className}
        >
          <IdentityProviderSettings
            clusterId={clusterId}
            organizationId={organizationId}
            setEnableFinishStep={setEnableFinishStep}
          />
        </WizardStep>
      )}

      {isImporting && wizardStep === 3 && (
        <WizardStep
          title="Setup Configuration"
          subtitle="Welcome to Pomerium Zero!"
          description="Import your existing configuration based on your deployment"
          onPrevStep={() => setWizardStep(2)}
          onNextStep={() => setWizardStep(4)}
          className={className}
        >
          <ImportTabs clusterToken={clusterToken || ""} />
        </WizardStep>
      )}
      {!isImporting && wizardStep === 3 && (
        <WizardStep
          title="Setup Configuration"
          subtitle="Welcome to Pomerium Zero!"
          description="Install Pomerium alongside your deployment."
          onPrevStep={() => setWizardStep(2)}
          onNextStep={() => setWizardStep(4)}
          className={className}
        >
          <SystemTypeConfigurationInfo
            systemType={systemType}
            clusterToken={clusterToken || ""}
            fqdn={cluster?.fqdn || ""}
            settings={settings}
          />
        </WizardStep>
      )}

      {wizardStep === 4 && (
        <WizardStep
          title="Finish Connection"
          subtitle="Welcome to Pomerium Zero!"
          description="Follow the instructions below to connect with Pomerium Zero."
          onPrevStep={() => setWizardStep(3)}
          onFinishStep={() => {
            sendStandardEvent("Set System Type", "onboarding", user, {
              system: systemType,
            });
            onFinishStep();
          }}
          // TODO: enable based on `ClusterStatus` [ENG-2300]
          enableFinishStep
          className={className}
        >
          <SystemTypeFinishInfo
            systemType={isImporting ? "import" : systemType}
            clusterToken={clusterToken ?? ""}
          />
          {systemType === "kubernetes" &&
            organizationId &&
            cluster &&
            clusterId && (
              <>
                <p>Enter the load balancer IP address to complete setup:</p>
                <span className="flex w-full gap-2">
                  <Input
                    type="text"
                    placeholder="e.g. 10.1.2.3"
                    onChange={(e) =>
                      setKubernetesLoadBalancerIp(e.target.value)
                    }
                  />
                  <Button
                    variant="default"
                    onClick={() =>
                      updateCluster({
                        organizationId,
                        clusterId,
                        clusterProperties: {
                          name: cluster.name,
                          manualOverrideIpAddress:
                            kubernetesLoadBalancerIp || undefined,
                        },
                      })
                    }
                  >
                    Update
                  </Button>
                </span>
              </>
            )}
          <ClusterStatus
            cluster={cluster}
            organizationId={organizationId}
            setAddressAndPort={(
              address: string | undefined,
              port: string | undefined,
            ) => {
              handleChangeAddressAndPort?.(address, port, systemType);
            }}
          />
          <Alert>
            <Info />
            <AlertTitle>Important Information</AlertTitle>
            <AlertDescription>
              Pomerium requires an internet-accessible server and the ability to
              bind port 443.
            </AlertDescription>
          </Alert>
        </WizardStep>
      )}
    </>
  );
}

function SystemTypeCardOptions({
  systemType,
  setSystemType,
}: {
  systemType: string;
  setSystemType: (sys: SystemType) => void;
}) {
  const settings = useSettings();
  const iconColor = settings.paletteMode
    ? settings.paletteMode === "light"
      ? "%238E51FF"
      : "white"
    : "purple";

  const systemOptions = [
    {
      value: "docker",
      label: "Docker",
      description:
        "Use `compose.yaml` to setup Pomerium Zero with your Docker container.",
      icon: `https://api.iconify.design/mdi/docker.svg?color=${iconColor}`,
    },
    {
      value: "linux",
      label: "Linux",
      description:
        "Use a shell script to install directly to your Linux machine.",
      icon: `https://api.iconify.design/mingcute/linux-fill.svg?color=${iconColor}`,
    },
    {
      value: "kubernetes",
      label: "Kubernetes",
      description: "Install Pomerium inside your Kubernetes cluster.",
      icon: `https://api.iconify.design/mdi/kubernetes.svg?color=${iconColor}`,
    },
    {
      value: "manual",
      label: "Manual",
      description: "Install Pomerium in accordance with your environment",
      icon: `https://api.iconify.design/bxs/wrench.svg?color=${iconColor}`,
    },
  ];

  return (
    <RadioGroup
      value={systemType}
      onValueChange={setSystemType}
      className="grid grid-cols-1 xl:grid-cols-2 gap-4 w-full"
    >
      {systemOptions.map((sys) => (
        <RadioCard
          key={`sysOpt_${sys.value}`}
          value={sys.value}
          title={sys.label}
          description={sys.description}
          icon={sys.icon}
          selected={systemType === sys.value}
          onSelect={() => setSystemType(sys.value as SystemType)}
        />
      ))}
    </RadioGroup>
  );
}

type SystemTypeConfigurationInfoProps = {
  systemType: SystemType;
  clusterToken: string;
  fqdn: string;
  settings?: Settings;
};

function SystemTypeConfigurationInfo({
  systemType,
  clusterToken,
  fqdn,
  settings,
}: SystemTypeConfigurationInfoProps) {
  const port = settings?.address?.split(":")?.pop() || "443";
  const coreVersion = import.meta.env.VITE_POMERIUM_CORE_VERSION;
  const [rpmManager, setRpmManager] = useState<"yum" | "dnf">("yum");

  switch (systemType) {
    case "docker": {
      const code = `services:
  pomerium:
    image: pomerium/pomerium:${coreVersion || "main"}
    ports:
      - ${port}:${port}
    restart: always
    environment:
      POMERIUM_ZERO_TOKEN: ${clusterToken}
      XDG_CACHE_HOME: /var/cache
    volumes:
      - pomerium-cache:/var/cache
    networks:
      main:
        aliases:
          - verify.${fqdn}
  verify:
    image: pomerium/verify:latest
    networks:
      main:
        aliases:
          - verify

networks:
  main: {}

volumes:
  pomerium-cache:
      `;

      return (
        <>
          <p>
            Add the following snippet into a{" "}
            <code className="border px-2.5 py-1.5 rounded-lg">
              compose.yaml
            </code>{" "}
            file in your Docker project:
          </p>
          <CodeSnippet code={code} copyInfo="dockerCompose" />
        </>
      );
    }

    case "linux": {
      const linuxScript = `curl https://console.pomerium.app/install.bash | env POMERIUM_ZERO_TOKEN=${clusterToken} bash -s install`;

      return (
        <>
          <p>Install Pomerium Core with the following script:</p>
          <CodeSnippet code={linuxScript} copyInfo="bashInstall" />
        </>
      );
    }

    case "kubernetes": {
      const installCode = `kubectl apply -k github.com/pomerium/pomerium/k8s/zero${coreVersion ? `\\?ref=${coreVersion}` : ""}`;
      const secretCode = `apiVersion: v1
kind: Secret
metadata:
  name: pomerium
  namespace: pomerium-zero
type: Opaque
stringData:
  pomerium_zero_token: ${clusterToken}`;
      const applySecretCode = `kubectl apply -f pomerium-secret.yaml`;

      return (
        <>
          <p>Install Pomerium into your Kubernetes cluster:</p>
          <CodeSnippet code={installCode} copyInfo="kubernetesInstall" />
          <p>
            Create{" "}
            <code className="px-2 py-1 border rounded-lg mx-0.5">
              pomerium-secret.yaml
            </code>{" "}
            to store the token used to authenticate your installation to
            Pomerium Zero:
          </p>
          <CodeSnippet code={secretCode} copyInfo="kubernetesSecret" />
          <p>Then apply the secret to your Kubernetes cluster:</p>
          <CodeSnippet
            code={applySecretCode}
            copyInfo="kubernetesApplySecret"
          />
        </>
      );
    }

    case "manual": {
      const rpmRepo = `dnf install yum-utils pygpgme
rpm --import 'https://dl.cloudsmith.io/public/pomerium/pomerium/gpg.6E388440B94E1407.key'
curl -1sLf 'https://dl.cloudsmith.io/public/pomerium/pomerium/config.rpm.txt?distro=fedora&codename=29&dnf_version=4' > /tmp/pomerium-pomerium.repo`;

      const yumAddRepo = `yum-config-manager --add-repo '/tmp/pomerium-pomerium.repo'`;
      const yumEnable = `yum -q makecache -y --disablerepo='*' --enablerepo='pomerium-pomerium'`;

      const dnf4AddRepo = `dnf config-manager --add-repo '/tmp/pomerium-pomerium.repo'`;
      const dnf5AddRepo = `dnf config-manager addrepo --from-repofile='/tmp/pomerium-pomerium.repo'`;
      const dnfEnable = `dnf -q makecache -y --disablerepo='*' --enablerepo='pomerium-pomerium' --enablerepo='pomerium-pomerium-source'`;

      const aptInit = `apt-get install -y debian-keyring  # debian only
apt-get install -y debian-archive-keyring  # debian only
apt-get install -y apt-transport-https

# For Debian Stretch, Ubuntu 16.04 and later
keyring_location=/usr/share/keyrings/pomerium-pomerium-archive-keyring.gpg
# For Debian Jessie, Ubuntu 15.10 and earlier
keyring_location=/etc/apt/trusted.gpg.d/pomerium-pomerium.gpg`;

      const aptKeyRepo = `curl -1sLf 'https://dl.cloudsmith.io/public/pomerium/pomerium/gpg.6E388440B94E1407.key' |  gpg --dearmor >> \${keyring_location}
curl -1sLf 'https://dl.cloudsmith.io/public/pomerium/pomerium/config.deb.txt?distro=ubuntu&codename=xenial&component=main' > /etc/apt/sources.list.d/pomerium-pomerium.list
sudo chmod 644 \${keyring_location}
sudo chmod 644 /etc/apt/sources.list.d/pomerium-pomerium.list
apt-get update`;

      const gitClone = `git clone https://github.com/pomerium/pomerium.git $HOME/pomerium
cd $HOME/pomerium`;

      const generateLocalCerts = `go install filippo.io/mkcert@latest
mkcert -install
mkcert '*.localhost.pomerium.io'`;

      return (
        <>
          <p>
            Install Pomerium Core on your machine with your preferred method:
          </p>
          <Tabs defaultValue="apt">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="apt">Debian</TabsTrigger>
              <TabsTrigger value="rpm">RPM (Yum, Dnf)</TabsTrigger>
              <TabsTrigger value="source">Source</TabsTrigger>
            </TabsList>

            <TabsContent value="rpm" className="flex flex-col gap-4 my-4">
              <p>
                Install necessary packages and import the key and repository
                configuration:
              </p>
              <CodeSnippet code={rpmRepo} copyInfo="rpmRepoCurl" />
              <p>
                Follow the rest of the instructions based on your package
                manager:
              </p>
              <RadioGroup
                defaultValue="yum"
                onValueChange={(value) => setRpmManager(value as "yum" | "dnf")}
                className="flex gap-2 items-center"
              >
                <span className="flex items-center gap-2">
                  <RadioGroupItem value="yum" id="yumOption" />
                  <Label htmlFor="yumOption">
                    <code className="px-2 py-1 border rounded-lg mx-0.5">
                      yum
                    </code>
                  </Label>
                </span>
                <span className="flex items-center gap-2">
                  <RadioGroupItem value="dnf" id="dnfOption" />
                  <Label htmlFor="dnfOption">
                    <code className="px-2 py-1 border rounded-lg mx-0.5">
                      dnf
                    </code>
                  </Label>
                </span>
              </RadioGroup>

              {rpmManager === "yum" && (
                <>
                  <p>
                    Add the Pomerium repository to your package manager using{" "}
                    <code className="px-2 py-1 border rounded-lg mx-0.5">
                      yum-config-manager
                    </code>
                    :
                  </p>
                  <CodeSnippet code={yumAddRepo} copyInfo="yumAddRepo" />
                  <p>Then enable the Pomerium repository:</p>
                  <CodeSnippet code={yumEnable} copyInfo="yumEnable" />
                  <p>Finally install Pomerium:</p>
                  <CodeSnippet
                    code="yum install pomerium"
                    copyInfo="yumInstall"
                  />
                </>
              )}

              {rpmManager === "dnf" && (
                <>
                  <p>
                    Add the Pomerium repository to your package manager using{" "}
                    <code className="px-2 py-1 border rounded-lg mx-0.5">
                      dnf config-manager
                    </code>
                    :
                  </p>
                  <p>
                    For{" "}
                    <code className="px-2 py-1 border rounded-lg mx-0.5">
                      dnf
                    </code>{" "}
                    version 4.x or below:
                  </p>
                  <CodeSnippet code={dnf4AddRepo} copyInfo="dnf4AddRepo" />
                  <p>
                    For{" "}
                    <code className="px-2 py-1 border rounded-lg mx-0.5">
                      dnf
                    </code>{" "}
                    version 5.x or above:
                  </p>
                  <CodeSnippet code={dnf5AddRepo} copyInfo="dnf5AddRepo" />
                  <p>Then enable the Pomerium repository:</p>
                  <CodeSnippet code={dnfEnable} copyInfo="dnfEnable" />
                  <p>Finally install Pomerium:</p>
                  <CodeSnippet
                    code="dnf install pomerium"
                    copyInfo="dnfInstall"
                  />
                </>
              )}
            </TabsContent>

            <TabsContent value="apt" className="flex flex-col gap-4 my-4">
              <p>
                Install necessary packages and set the keyring location based on
                your Debian version:
              </p>
              <CodeSnippet code={aptInit} copyInfo="aptInit" />
              <p>Add the Pomerium key and repository to APT:</p>
              <CodeSnippet code={aptKeyRepo} copyInfo="aptKeyRepo" />
              <p>Finally install Pomerium:</p>
              <CodeSnippet code="apt install pomerium" copyInfo="aptInstall" />
            </TabsContent>

            <TabsContent value="source" className="flex flex-col gap-4 my-4">
              <p>Clone the repository from GitHub:</p>
              <CodeSnippet code={gitClone} copyInfo="gitRepoClone" />
              <Accordion type="single">
                <AccordionItem value="sourceOptional">
                  <AccordionTrigger>
                    <p>
                      (Optional) Generate local certificates for local
                      development:
                    </p>
                  </AccordionTrigger>

                  <AccordionContent>
                    <CodeSnippet
                      code={generateLocalCerts}
                      copyInfo="generateLocalCerts"
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <p>Build Pomerium Core from the source:</p>
              <CodeSnippet code="make" copyInfo="makeCore" />
              <p>
                This will compile the{" "}
                <code className="px-2 py-1 border rounded-lg mx-0.5">
                  pomerium
                </code>
                binary under{" "}
                <code className="px-2 py-1 border rounded-lg mx-0.5">
                  ./bin
                </code>{" "}
                directory. If you don't have test prerequisites installed
                (Docker, Redis, etc.), run
                <code className="px-2 py-1 border rounded-lg mx-0.5">
                  make build
                </code>{" "}
                to skip them.
              </p>
            </TabsContent>
          </Tabs>
        </>
      );
    }
  }
}

type SystemTypeFinishInfoProps = {
  systemType: SystemType | "import";
  clusterToken: string;
};

function SystemTypeFinishInfo({
  systemType,
  clusterToken,
}: SystemTypeFinishInfoProps) {
  switch (systemType) {
    case "docker":
      return (
        <>
          <p>Run the following command to start your container:</p>
          <CodeSnippet code={`docker compose up -d`} copyInfo="dockerRun" />
        </>
      );
    case "kubernetes":
      return (
        <>
          <p>
            Fetch your Kubernetes load balancer IP address using the following
            command:
          </p>
          <CodeSnippet
            code={
              "kubectl get svc/pomerium-proxy -n pomerium-zero -o=jsonpath='{.status.loadBalancer.ingress[0].ip}'"
            }
            copyInfo="kubernetesLoadBalancerIp"
          />
        </>
      );
    case "manual":
      return (
        <>
          <p>
            Run the{" "}
            <code className="px-2 py-1 border rounded-lg mx-0.5">pomerium</code>{" "}
            binary with the{" "}
            <code className="px-2 py-1 border rounded-lg mx-0.5">
              POMERIUM_ZERO_TOKEN
            </code>
            that was saved earlier as an environment variable:
          </p>
          <CodeSnippet
            code={`env POMERIUM_ZERO_TOKEN=${clusterToken} | pomerium`}
            copyInfo="manualRun"
          />
        </>
      );
    case "linux":
    default:
      return (
        <p>
          The previous step should be enough for Pomerium to get started. Hang
          on until Zero is connected.
        </p>
      );
  }
}

function ImportTabs({ clusterToken }: { clusterToken: string }) {
  // Linux
  const [binaryPath, setBinaryPath] = useState("./pomerium");
  const [configPath, setConfigPath] = useState("/path/to/config.yaml");
  const linuxImportCommand = `${binaryPath} zero import --token ${clusterToken} --config ${configPath}`;

  // Docker
  const [containerName, setContainerName] = useState("pomerium");
  const dockerImportCommand = `docker exec -it ${containerName} /bin/pomerium zero import --token ${clusterToken}`;

  return (
    <>
      <p>Choose between your existing deployment environment:</p>
      <Tabs defaultValue="docker">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="docker">Docker</TabsTrigger>
          <TabsTrigger value="linux">Linux</TabsTrigger>
        </TabsList>
        <TabsContent value="docker" className="flex flex-col gap-4 my-4">
          <p>
            Update the command with existing Docker container with Pomerium:
          </p>
          <span className="space-y-2">
            <Label htmlFor="containerName">Container Name/ID</Label>
            <Input
              id="containerName"
              type="text"
              placeholder="pomerium"
              onChange={(e) => setContainerName(e.target.value)}
            />
          </span>
          <p>
            Run the following command to import your existing configuration to
            Pomerium Zero:
          </p>
          <CodeSnippet
            code={dockerImportCommand}
            copyInfo="dockerImportCommand"
          />
        </TabsContent>
        <TabsContent value="linux" className="flex flex-col gap-4 my-4">
          <p>Update the command with existing Pomerium paths:</p>
          <span className="space-y-2">
            <Label htmlFor="binaryPath">Pomerium Binary Path</Label>
            <Input
              id="configPath"
              type="text"
              placeholder="./pomerium"
              onChange={(e) => setBinaryPath(e.target.value)}
            />
          </span>
          <span className="space-y-2">
            <Label htmlFor="configPath">Configuration Path</Label>
            <Input
              id="configPath"
              type="text"
              placeholder="/path/to/config.yaml"
              onChange={(e) => setConfigPath(e.target.value)}
            />
          </span>
          <p>
            Run the following command to import your existing configuration to
            Pomerium Zero:
          </p>
          <CodeSnippet
            code={linuxImportCommand}
            copyInfo="linuxImportCommand"
          />
        </TabsContent>
      </Tabs>
    </>
  );
}
